import axios from "axios";
import authHeader from "./auth-header";
import AuthService from "./auth.service";
import { config } from "../config";

const base = `${config["apiUrl"]}serviceOrders/dg`;

function handle(promise) {
  return promise
    .then((response) => (response.status === 200 ? response.data : null))
    .catch((error) => {
      if (error.response && error.response.status === 401) {
        AuthService.logout();
        window.location = "/login";
      }
      console.log("DG ERROR:: ", error.response ? error.response.data : error);
      return error.response ? error.response.data : null;
    });
}

function normalizeCatalogItem(item, codeKeys = []) {
  const codeKey =
    codeKeys.find((key) => item && item[key] !== undefined) ||
    Object.keys(item || {}).find((key) => key.toLowerCase().startsWith("cod"));
  const descriptionKey =
    ["description", "descripcion"].find(
      (key) => item && item[key] !== undefined
    ) || null;

  return {
    ...item,
    code: codeKey ? item[codeKey] : null,
    description: descriptionKey ? item[descriptionKey] : "",
  };
}

function normalizeCatalogResponse(response, codeKeys = []) {
  if (!response || !Array.isArray(response.items)) {
    return response;
  }

  return {
    ...response,
    items: response.items.map((item) => normalizeCatalogItem(item, codeKeys)),
  };
}

class DgService {
  // Phase I — sync / list / pat
  syncFromApi() {
    return handle(axios.get(base, { headers: authHeader() }));
  }
  listAccepted() {
    return handle(axios.get(`${base}/list/accepted`, { headers: authHeader() }));
  }
  countAccepted() {
    return handle(axios.get(`${base}/list/accepted/count`, { headers: authHeader() }));
  }
  listWithoutPat() {
    return handle(axios.get(`${base}/list/withoutPat`, { headers: authHeader() }));
  }
  countWithoutPat() {
    return handle(axios.get(`${base}/list/withoutPat/count`, { headers: authHeader() }));
  }
  createPat(id) {
    return handle(axios.patch(`${base}/${id}/createPat`, {}, { headers: authHeader() }));
  }
  getByPatId(patId) {
    return handle(axios.get(`${base}/pat/${patId}`, { headers: authHeader() }));
  }
  refresh(id) {
    return handle(axios.patch(`${base}/${id}/refresh`, {}, { headers: authHeader() }));
  }

  // Phase II — transport / reception / return / visit
  savePickup(id, payload) {
    return handle(axios.patch(`${base}/${id}/transport/pickup`, payload, { headers: authHeader() }));
  }
  saveReception(id, payload) {
    return handle(axios.patch(`${base}/${id}/transport/reception`, payload, { headers: authHeader() }));
  }
  saveReturn(id, payload) {
    const body = {
      ...payload,
      send_date: payload.send_date || payload.return_date || null,
    };

    return handle(axios.patch(`${base}/${id}/transport/return`, body, { headers: authHeader() }));
  }
  updateReturnTracking(id, payload) {
    return handle(axios.patch(`${base}/${id}/transport/return-tracking`, payload, { headers: authHeader() }));
  }
  saveTransportObservations(id, payload) {
    return handle(axios.patch(`${base}/${id}/transport/observations`, payload, { headers: authHeader() }));
  }
  saveVisit(id, payload) {
    const body = {
      ...payload,
      scheduled_at: payload.scheduled_at || payload.visit_date || null,
    };

    return handle(axios.patch(`${base}/${id}/visit`, body, { headers: authHeader() }));
  }

  // Phase II — diagnostics
  saveRepairDiagnostic(id, payload) {
    return handle(axios.patch(`${base}/${id}/diagnostic/repair`, payload, { headers: authHeader() }));
  }
  saveDirectChangeDiagnostic(id, payload) {
    return handle(axios.patch(`${base}/${id}/diagnostic/direct-change`, payload, { headers: authHeader() }));
  }
  saveMatrixDiagnostic(id, payload) {
    return handle(axios.patch(`${base}/${id}/diagnostic/matrix`, payload, { headers: authHeader() }));
  }
  saveTechnicalValuationDiagnostic(id, payload) {
    return handle(axios.patch(`${base}/${id}/diagnostic/technical-valuation`, payload, { headers: authHeader() }));
  }
  saveSoftCallDiagnostic(id, payload) {
    return handle(axios.patch(`${base}/${id}/diagnostic/soft-call`, payload, { headers: authHeader() }));
  }
  saveRefurbishDiagnostic(id, payload) {
    return handle(axios.patch(`${base}/${id}/diagnostic/refurbish`, payload, { headers: authHeader() }));
  }
  saveDeniedRepair(id, payload) {
    return handle(axios.patch(`${base}/${id}/diagnostic/denied`, payload, { headers: authHeader() }));
  }
  saveIrreparable(id, payload) {
    return handle(axios.patch(`${base}/${id}/diagnostic/irreparable`, payload, { headers: authHeader() }));
  }
  savePendingParts(id, payload) {
    return handle(axios.patch(`${base}/${id}/diagnostic/pending-parts`, payload, { headers: authHeader() }));
  }
  saveRefurbishImei(id, payload) {
    return handle(axios.patch(`${base}/${id}/diagnostic/refurbish-imei`, payload, { headers: authHeader() }));
  }
  deleteResolution(id) {
    return handle(axios.delete(`${base}/${id}/diagnostic`, { headers: authHeader() }));
  }

  // Phase II — letters / communication
  generateRefurbishLetter(id, payload) {
    return handle(axios.patch(`${base}/${id}/letters/refurbish`, payload, { headers: authHeader() }));
  }
  generateDenialLetter(id, payload) {
    return handle(axios.patch(`${base}/${id}/letters/denial`, payload, { headers: authHeader() }));
  }
  sendCommunication(id, payload) {
    const body = {
      ...payload,
      department_code: payload.department_code || payload.type || null,
    };

    return handle(axios.patch(`${base}/${id}/communication`, body, { headers: authHeader() }));
  }

  // Phase III — close / invoice
  closeClaim(id, payload) {
    const body =
      typeof payload === "string" ? { status_code: payload } : payload;

    return handle(axios.patch(`${base}/${id}/close`, body, { headers: authHeader() }));
  }
  createInvoice(id, payload) {
    const body = {
      ...payload,
      amount: payload.amount !== undefined ? payload.amount : payload.total,
    };

    return handle(axios.patch(`${base}/${id}/invoice`, body, { headers: authHeader() }));
  }

  // Catalogs
  getDenialReasons() {
    return handle(axios.get(`${base}/catalogs/denial-reasons`, { headers: authHeader() })).then((response) =>
      normalizeCatalogResponse(response, ["code", "codMotivoDenegacion"])
    );
  }
  getIrreparableReasons() {
    return handle(axios.get(`${base}/catalogs/irreparable-reasons`, { headers: authHeader() })).then((response) =>
      normalizeCatalogResponse(response, ["code", "codMotivoIrreparable"])
    );
  }
  getApplianceFamilyFailures() {
    return handle(axios.get(`${base}/catalogs/family-failures`, { headers: authHeader() })).then((response) =>
      normalizeCatalogResponse(response)
    );
  }
  getCoverages() {
    return handle(axios.get(`${base}/catalogs/coverages`, { headers: authHeader() })).then((response) =>
      normalizeCatalogResponse(response)
    );
  }
}

export default new DgService();
